import React, { useCallback, useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Typography } from "@mui/material";
import { useRoutes } from "react-router-dom";
import routes from "./routes";
import { useDispatch, useSelector } from "./store";
import { fetchDbUser, fetchProfile, logout } from "./slices/auth";
import "@aws-amplify/ui-react/styles.css";
import theme from "./theme";
import Toast from "./components/base/Toast";
import { handleToastState, toggleLicense } from "./slices/shared";
import AmplifyAuth from "./components/views/Layout/AmplifyAuth";
import { fetchAuthSession } from "aws-amplify/auth";
import Backdrop from "@mui/material/Backdrop";
import { Authenticator } from "@aws-amplify/ui-react";
import { fetchSettings } from "./slices/settings";
import { getHomeDashboard } from "./slices/dashboard";
import { fetchSiteAlerts } from "./slices/alert";
import { Applications } from "./types/company";
import checkRole, { UserRoles } from "./helpers/checkRole";
import BackdropLoader from "./components/base/BackdropLoader";
interface Props {}

const App: React.FC<Props> = () => {
  const dispatch = useDispatch();

  const { homeDashboard } = useSelector((state) => state.dashboard);
  const { loggedInUser, profileUser, userCompany } = useSelector(
    (state) => state.auth,
  );
  const { toastStatus, globalLoadingState, productLicensed } = useSelector(
    (state) => state.shared,
  );
  const { settings } = useSelector((state) => state.settings);

  const content = useRoutes(routes(productLicensed));

  const handleCloseToast = () => {
    dispatch(handleToastState({ open: "none", message: "" }));
  };

  const loadProfile = useCallback(async () => {
    if (!profileUser) {
      dispatch(fetchProfile());
    }
  }, [dispatch, profileUser]);

  const loadDbUser = useCallback(async () => {
    if (!!profileUser && !loggedInUser) {
      dispatch(fetchDbUser(profileUser));
    }
  }, [dispatch, profileUser, loggedInUser]);

  const loadLicense = useCallback(async () => {
    if (userCompany && productLicensed === undefined) {
      let companyLicenses = userCompany?.licenses;
      if (companyLicenses?.length! > 0) {
        const detectorLicense = companyLicenses?.find(
          (x) => x.productId === Applications.Detector,
        );

        if (detectorLicense?.licensed) {
          return dispatch(toggleLicense(true));
        }
      }
      return dispatch(toggleLicense(false));
    }
  }, [dispatch, productLicensed, userCompany]);

  const loadCompanySettings = useCallback(async () => {
    if (loggedInUser && !settings && productLicensed === true) {
      await dispatch(fetchSettings());
    }
  }, [dispatch, loggedInUser, settings, productLicensed]);

  const loadHomeDashboard = useCallback(async () => {
    if (settings && !homeDashboard) {
      await dispatch(getHomeDashboard());
    }
  }, [dispatch, settings, homeDashboard]);

  const loadAlerts = useCallback(async () => {
    if (
      productLicensed === true &&
      homeDashboard &&
      settings?.notificationSettings?.siteNotifications === true
    ) {
      dispatch(fetchSiteAlerts());
    }
  }, [
    dispatch,
    homeDashboard,
    productLicensed,
    settings?.notificationSettings?.siteNotifications,
  ]);

  const handleLoadApp = useCallback(async () => {
    const session = await fetchAuthSession();

    if (session?.tokens) {
      try {
        await Promise.all([
          loadProfile(),
          loadDbUser(),
          loadLicense(),
          loadCompanySettings(),
          loadHomeDashboard(),
          loadAlerts(),
        ]);
      } catch (error) {
        dispatch(logout());
      }
    } else {
      window.location.href = `${process.env.REACT_APP_AUTH_URL}/app/detector`;
    }
  }, [
    dispatch,
    loadProfile,
    loadDbUser,
    loadLicense,
    loadCompanySettings,
    loadHomeDashboard,
    loadAlerts,
  ]);

  useEffect(() => {
    const interval = setInterval(loadAlerts, 10000); //change from 5000

    return () => clearInterval(interval);
  }, [loadAlerts]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Backdrop
        open={productLicensed === false}
        sx={{
          zIndex: theme.zIndex.drawer + 2,
          backgroundColor: "rgba(38,84,158,0.5)",
        }}
      >
        {checkRole(loggedInUser!, UserRoles.GlobalAdmin) ? (
          <Typography variant="h3">
            No account license found. Visit the{" "}
            <a
              href={`${process.env.REACT_APP_AUTH_URL}/app/apps`}
              style={{ color: theme.palette.primary.main, fontWeight: "bold" }}
            >
              admin portal
            </a>{" "}
            to start free trial or update registration.
          </Typography>
        ) : (
          <Typography>
            Account inactive. Please contact your administrator.
          </Typography>
        )}
      </Backdrop>
      <BackdropLoader
        open={globalLoadingState.isLoading && productLicensed !== false}
      />
      <Toast
        open={toastStatus.open !== "none"}
        message={toastStatus.message}
        type={toastStatus.open !== "none" ? toastStatus.open : "info"}
        handleClose={handleCloseToast}
      />
      <Authenticator.Provider>
        <AmplifyAuth loadApp={handleLoadApp}>{content}</AmplifyAuth>
      </Authenticator.Provider>
    </ThemeProvider>
  );
};

export default App;
